import React, { useEffect, useRef, useState } from "react";
import { useSupportContacts, whatsappUrl } from "../../utils/useSupportContacts";

/* =====================================================================
   Floating support
   =====================================================================
   The WhatsApp / Telegram contacts configured in Admin -> Accounts.

   Desktop: the two round buttons, bottom-right, as before.
   Phone (< 992px, where the bottom tab bar is shown): one "Support"
   button that sits above the tab bar and opens the two choices. Two
   60px discs stacked on a 390px screen buried one under the tab bar and
   covered odds and footer links with the other (#4).

   Several views mount this component (FrontPage, Home, CasinoGames,
   Withdraw...), sometimes more than one at once. Only the first mounted
   instance renders, so there is never a second stack under the first.
   ===================================================================== */

let owner = null;
const owners = new Set();
const claim = (id, notify) => {
  owners.add(notify);
  if (owner === null) owner = id;
  owners.forEach((fn) => fn());
};
const release = (id, notify) => {
  owners.delete(notify);
  if (owner === id) owner = null;
  owners.forEach((fn) => fn());
};
let nextId = 1;

const TelegramIcon = ({ size = 30 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="#fff" aria-hidden="true">
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71l-4.13-3.05-1.99 1.93c-.23.23-.42.42-.83.42z" />
  </svg>
);

const WhatsAppIcon = ({ size = 30 }) => (
  <img src="/assets/images/whatsappImages.svg" alt="" width={size} height={size} aria-hidden="true" />
);

const open = (url) => {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

const WhatsAppButton = () => {
  const contacts = useSupportContacts();
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = nextId++;
  const [, force] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const id = idRef.current;
    const notify = () => force((n) => n + 1);
    claim(id, notify);
    return () => release(id, notify);
  }, []);

  // Tap anywhere else closes the phone menu.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  if (owner !== idRef.current || !contacts) return null;

  const wa = whatsappUrl(contacts.whatsapp);
  const tg = contacts.telegram;
  if (!wa && !tg) return null;

  // With a single channel configured the phone button goes straight there.
  const onFab = () => {
    if (wa && tg) setMenuOpen((v) => !v);
    else open(wa || tg);
  };

  return (
    <div className="rb-support" ref={rootRef}>
      {/* Desktop: one disc per channel */}
      <div className="rb-support__discs">
        {tg && (
          <button
            type="button"
            className="rb-support__disc rb-support__disc--tg"
            onClick={() => open(tg)}
            aria-label="Telegram support"
            title="Telegram support"
          >
            <TelegramIcon size={32} />
          </button>
        )}
        {wa && (
          <button
            type="button"
            className="rb-support__disc rb-support__disc--wa"
            onClick={() => open(wa)}
            aria-label="WhatsApp support"
            title="Chat with us on WhatsApp"
          >
            <WhatsAppIcon size={32} />
          </button>
        )}
      </div>

      {/* Phone: one button above the tab bar */}
      <div className="rb-support__mobile">
        {menuOpen && (
          <div className="rb-support__menu" role="menu">
            {wa && (
              <button
                type="button"
                role="menuitem"
                className="rb-support__option rb-support__option--wa"
                onClick={() => {
                  setMenuOpen(false);
                  open(wa);
                }}
              >
                <WhatsAppIcon size={22} /> WhatsApp
              </button>
            )}
            {tg && (
              <button
                type="button"
                role="menuitem"
                className="rb-support__option rb-support__option--tg"
                onClick={() => {
                  setMenuOpen(false);
                  open(tg);
                }}
              >
                <TelegramIcon size={22} /> Telegram
              </button>
            )}
          </div>
        )}
        <button
          type="button"
          className="rb-support__fab"
          onClick={onFab}
          aria-haspopup={wa && tg ? "menu" : undefined}
          aria-expanded={wa && tg ? menuOpen : undefined}
          aria-label="Contact support"
        >
          <i className={menuOpen ? "fa fa-times" : "fa fa-headphones"} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default WhatsAppButton;
